import {
    BooleanTableColumn,
    ColumnFilterTypeConfig,
    DateTableColumn,
    DecimalTableColumn,
    FieldOfType,
    HyperlinkTableColumn,
    NumberTableColumn,
    StringTableColumn,
    TableColumnAlign,
} from '@fl/cmsch-fe-library/dist/components/tables/types';

interface BaseOptions {
    width: number;
    align?: TableColumnAlign;
    verticalName?: boolean;
    customFiltersOnly?: boolean;
}

interface StringColumnOptions<T> extends BaseOptions {
    field: FieldOfType<T, string>;
    filterTypes?: Array<ColumnFilterTypeConfig>;
    sortable?: boolean;
}

interface NumberColumnOptions<T> extends BaseOptions {
    field: FieldOfType<T, number>;
    filterTypes?: Array<ColumnFilterTypeConfig>;
}

interface BooleanColumnOptions<T> extends BaseOptions {
    field: FieldOfType<T, boolean>;
    disableTooltip?: boolean;
}

interface HyperlinkColumnOptions<T> extends BaseOptions {
    field: FieldOfType<T, string>;
    filterTypes?: Array<ColumnFilterTypeConfig>;
    valueToUrl(value: string, row: T): string | null;
}

interface DateColumnOptions<T> extends BaseOptions {
    field: FieldOfType<T, string>;
    filterTypes?: Array<ColumnFilterTypeConfig>;
}

interface DecimalColumnOptions<T> extends BaseOptions {
    field: FieldOfType<T, number>;
    filterTypes?: Array<ColumnFilterTypeConfig>;
    fixedDecimals: number;
}

/**
 * Builds a hyperlink table column for string-backed values.
 *
 * @param field Table field mapped to the displayed value.
 * @param width Column width in pixels.
 * @param align Optional column alignment.
 * @param valueToUrl Resolver that maps the cell value and row into a URL.
 * @returns A hyperlink table column configuration.
 */
export const hyperlinkColumn = <T>({
    field,
    width,
    align,
    filterTypes,
    valueToUrl,
}: HyperlinkColumnOptions<T>): HyperlinkTableColumn<T> => ({
    field,
    type: 'hyperlink',
    valueToUrl: (value, row) => valueToUrl(String(value), row),
    openInNewTab: true,
    width,
    align,
    filterTypes,
});

/**
 * Builds a standard string table column.
 *
 * @param field Table field mapped to the displayed value.
 * @param width Column width in pixels.
 * @param align Optional column alignment.
 * @param verticalName Whether the column label can wrap vertically.
 * @param filterTypes Allowed filter widget types.
 * @param sortable Whether the column can be sorted.
 * @returns A string table column configuration.
 */
export const stringColumn = <T>({
    field,
    width,
    align,
    verticalName = true,
    filterTypes,
    sortable,
    customFiltersOnly,
}: StringColumnOptions<T>): StringTableColumn<T> => ({
    field,
    verticalName,
    type: 'string',
    width,
    align,
    filterTypes,
    sortable,
    customFiltersOnly,
});

/**
 * Builds a boolean table column.
 *
 * @param field Table field mapped to the displayed value.
 * @param width Column width in pixels.
 * @param align Optional column alignment.
 * @param verticalName Whether the column label can wrap vertically.
 * @param disableTooltip Whether the tooltip should be suppressed.
 * @returns A boolean table column configuration.
 */
export const booleanColumn = <T>({
    field,
    width,
    align,
    verticalName = true,
    disableTooltip,
}: BooleanColumnOptions<T>): BooleanTableColumn<T> => ({
    field,
    verticalName,
    type: 'boolean',
    width,
    align,
    disableTooltip,
});

/**
 * Builds a date table column.
 *
 * @param field Table field mapped to the displayed value.
 * @param width Column width in pixels.
 * @param align Optional column alignment.
 * @param verticalName Whether the column label can wrap vertically.
 * @param filterTypes Allowed filter widget types.
 * @returns A date table column configuration.
 */
export const dateColumn = <T>({
    field,
    width,
    align,
    verticalName = true,
    filterTypes = ['null'],
}: DateColumnOptions<T>): DateTableColumn<T> => ({
    field,
    verticalName,
    type: 'date',
    dateFormat: 'date',
    width,
    align,
    filterTypes,
});

/**
 * Builds a numeric table column.
 *
 * @param field Table field mapped to the displayed value.
 * @param width Column width in pixels.
 * @param align Optional column alignment.
 * @param verticalName Whether the column label can wrap vertically.
 * @param filterTypes Allowed filter widget types.
 * @returns A numeric table column configuration.
 */
export const numberColumn = <T>({
    field,
    width,
    align,
    verticalName = true,
    filterTypes = ['null'],
}: NumberColumnOptions<T>): NumberTableColumn<T> => ({
    field,
    verticalName,
    type: 'number',
    width,
    align,
    filterTypes,
});

/**
 * Builds a decimal table column with a fixed precision.
 *
 * @param field Table field mapped to the displayed value.
 * @param width Column width in pixels.
 * @param align Optional column alignment.
 * @param verticalName Whether the column label can wrap vertically.
 * @param filterTypes Allowed filter widget types.
 * @param fixedDecimals Number of fixed decimal places.
 * @returns A decimal table column configuration.
 */
export const decimalColumn = <T>({
    field,
    width,
    align,
    verticalName = true,
    filterTypes = ['null'],
    fixedDecimals,
}: DecimalColumnOptions<T>): DecimalTableColumn<T> => ({
    field,
    verticalName,
    type: 'decimal',
    width,
    align,
    fixedDecimals,
    filterTypes,
});
